import axios, { AxiosResponse } from "axios";
import { PatientUrl } from "./AUrl";

/*
The interface for a chat message.

@role: The role of the message sender ("user" for clinician, "system" for patient).
@content: The content of the message.
*/
export interface ChatMessage {
    role: "user" | "system";
    content: string;
}

/*
The payload structure for the chat request.

@patient_id: The ID of the patient.
@messages: The chat history.
@temperature: The model's randomness setting.
@max_tokens: The maximum number of tokens allowed in the response.
*/
export interface ChatRequest {
    patient_id: number;
    messages: ChatMessage[];
    temperature: number;
    max_tokens: number;
}

/*
The structure of a message response from the OpenAI API.

@role: The role of the AI response.
@content: The response text from the AI.
*/
export interface ChatResponseMessage {
    role: string;
    content: string;
}

/*
The structure of the OpenAI response.

@choices: An array of AI-generated messages.
*/
export interface ChatApiResponse {
    choices: { message: ChatResponseMessage }[];
}

export type ChatWithPatientType = (payload: ChatRequest, jwt?: string) => Promise<ChatApiResponse | { status: number; error: string }>;


/*
The function to send a chat request to the AI.

@payload: The chat request payload.
@jwt: The JWT of the authenticated user.

@returns: The API response containing AI-generated messages.
*/
export async function chatWithPatient(payload: ChatRequest, jwt?: string): Promise<ChatApiResponse | { status: number; error: string }> {
    const url = new PatientUrl().chat;

    if (!jwt) {
        return { status: 401, error: "JWT has expired" };
    }

    let response: AxiosResponse<ChatApiResponse | string>;
    try {
        response = await axios.post<ChatApiResponse | string>(url, payload, {
            headers: {
                "Content-Type": "application/json",
                "token": jwt,
            },
            validateStatus: () => true, // Resolve promise for all status codes
        });
    } catch (error) {
        return { status: 500, error: "Network or other error occurred" };
    }

    if (response.status === 200) {
        return response.data as ChatApiResponse;
    } else {
        return { status: response.status, error: response.data as string };
    }
}
